import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { jwtDecode } from 'jwt-decode';
import { config } from '../config';

export interface User {
  email: string;
  name: string;
  picture?: string;
}

interface DecodedGoogleIdToken {
  email: string;
  email_verified?: boolean;
  name?: string;
  picture?: string;
  exp: number;
  aud: string;
  iss: string;
  sub: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  signIn: (idToken: string) => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function decodeAndValidate(idToken: string): { user: User; expMs: number } | null {
  try {
    const claims = jwtDecode<DecodedGoogleIdToken>(idToken);
    const expMs = claims.exp * 1000;
    if (expMs <= Date.now()) return null;
    if (claims.aud !== config.oauthClientId) return null;
    if (!claims.email) return null;
    return {
      user: {
        email: claims.email,
        name: claims.name ?? claims.email,
        picture: claims.picture,
      },
      expMs,
    };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem(config.tokenStorageKey);
    if (stored) {
      const decoded = decodeAndValidate(stored);
      if (decoded) {
        setToken(stored);
        setUser(decoded.user);
      } else {
        localStorage.removeItem(config.tokenStorageKey);
      }
    }
    setIsLoading(false);
  }, []);

  const signIn = useCallback((idToken: string) => {
    const decoded = decodeAndValidate(idToken);
    if (!decoded) {
      throw new Error('Invalid Google ID token');
    }
    localStorage.setItem(config.tokenStorageKey, idToken);
    setToken(idToken);
    setUser(decoded.user);
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem(config.tokenStorageKey);
    setToken(null);
    setUser(null);
    window.location.assign('/login');
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, isLoading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
